import React from 'react';
import classnames from 'classnames';
import { Popup, TextInput, CompositeTextInput, Flex, Text } from '@leverege/ui-elements';
import PropTypes from 'prop-types';
import { v4 } from '@leverege/base62-util';
import S from './Geocoder.css';
const MAPBOX_APIKEY = process.env.MAPBOX_APIKEY;
const MAPBOX_QUERY_ENDPOINT = 'https://api.mapbox.com/search/searchbox/v1/suggest';
const MAPBOX_RETRIEVE_ENDPOINT = 'https://api.mapbox.com/search/searchbox/v1/retrieve';

/**
 * Searches a places API (e.g., mapbox places) for a string, and returns a list of matching
 * geocoded results. Can be used as a component with a search bar and results display
 * or static methods can be used to capture queries and results without mounting the
 * component.
 *
 * As a Component: <Geocoder onSelect={console.log} />
 *
 * As a static class: Geocoder.queryMapbox( 'Leverege HQ', { near : '71.234,-31.523' } )
 */
export default class Geocoder extends React.Component {
  /**
   * @param {string} searchTerm
   * @param {object} opts
   * @param {string} opts.near `lon,lat` : biases results to those nearest the given lon,lat
   * @param {string} opts.apiKey mapbox api key to use
   * @param {string} opts.endpoint mapbox places endpoint (default : https://api.tiles.mapbox.com/geocoding/v5/mapbox.places )
   * @returns {object} results: results.features is an array of the hits
   */
  static async queryMapbox(searchTerm, opts = {}) {
    const {
      near,
      apiKey = MAPBOX_APIKEY,
      endpoint = MAPBOX_QUERY_ENDPOINT,
      sessionToken
    } = opts;
    const uri = Geocoder.createQueryURI(searchTerm, {
      proximity: near,
      apiKey,
      endpoint,
      source: 'mapbox',
      sessionToken
    });
    const res = await fetch(uri, {
      headers: {
        Accept: 'application/json'
      }
    });
    const jsonRes = await res.json();
    return Geocoder.processQueryResults({
      type: 'mapbox',
      results: jsonRes
    });
  }
  static async retriveMapbox(placeId, opts = {}) {
    const {
      apiKey = MAPBOX_APIKEY,
      endpoint = MAPBOX_RETRIEVE_ENDPOINT,
      sessionToken
    } = opts;
    const uri = Geocoder.createRetrieveURI(placeId, {
      apiKey,
      endpoint,
      source: 'mapbox',
      sessionToken
    });
    const res = await fetch(uri, {
      headers: {
        Accept: 'application/json'
      }
    });
    const jsonRes = await res.json();
    return Geocoder.processRetrieveResults({
      type: 'mapbox',
      results: jsonRes
    });
  }

  /**
   * Creates a URI to query the source API
   * @param {string} searchTerm
   * @param {object} opts
   * @param {string} opts.proximity `lon,lat` : biases results to those nearest the given lon,lat
   * @param {string} opts.source which API to use (mapbox, google, bing) (currently only mapbox is supported)
   * @param {string} opts.endpoint API endpoint to use
   * @param {string} opts.apiKey API key for source
   */
  static createQueryURI(searchTerm, opts) {
    const {
      endpoint,
      proximity,
      source,
      apiKey,
      sessionToken
    } = opts;
    let uri;
    if (source === 'mapbox') {
      const prox = proximity ? `&proximity=${proximity}` : '';
      uri = `${endpoint}?q=${encodeURIComponent(searchTerm)}&access_token=${apiKey}&session_token=${sessionToken}${prox}`;
    }
    return uri;
  }
  static createRetrieveURI(placeId, opts) {
    const {
      endpoint,
      source,
      apiKey,
      sessionToken
    } = opts;
    let uri;
    if (source === 'mapbox') {
      uri = `${endpoint}/${encodeURIComponent(placeId)}?access_token=${apiKey}&session_token=${sessionToken}`;
    }
    return uri;
  }

  /**
   * Query function to use for a given api
   */
  static getQueryBySource(source) {
    switch (source.toLowerCase()) {
      case 'mapbox':
        return Geocoder.queryMapbox;
      default:
        return null;
    }
  }
  static getRetriverBySource(source) {
    switch (source.toLowerCase()) {
      case 'mapbox':
        return Geocoder.retriveMapbox;
      default:
        return null;
    }
  }

  /**
   * Function for normalizing results array from a given source
   * @param {object} data
   * @param {string} data.type the type of processor to use
   * @param {any} data.results the results to process
   * @returns {object[]} { id, location : <String>, position : { lat, lon } }
   */
  static processQueryResults(data) {
    const {
      results
    } = data;
    switch (data.type.toLowerCase()) {
      case 'mapbox':
        return {
          attribution: results.attribution,
          suggestions: results?.suggestions.map(f => ({
            ...f,
            location: f.name_preferred || f.name,
            id: f.mapbox_id
          })) || []
        };
      default:
        return [];
    }
  }
  static processRetrieveResults(data) {
    const {
      results
    } = data;
    switch (data.type.toLowerCase()) {
      case 'mapbox':
        {
          const f = results.features?.[0];
          return {
            ...f,
            position: {
              lat: f?.geometry?.coordinates?.[1],
              lon: f?.geometry?.coordinates?.[0]
            }
          };
        }
      default:
        return [];
    }
  }
  static propTypes = {
    className: PropTypes.string,
    /** CSS class for the wrapping <div> component */
    onChange: PropTypes.func.isRequired,
    /** What happens when a user selects a result from the list */
    onInputChange: PropTypes.func,
    /** What, besides querying the API, happens when a user submits a query */
    onResults: PropTypes.func,
    /** Called with query results */
    eventData: PropTypes.any,
    /** Returned as data field on select */
    inputVariant: PropTypes.string,
    /** Builder theme variant for TextInput */
    inputClass: PropTypes.string,
    /** CSS class for the TextInput search field */
    resultsVariant: PropTypes.string,
    /** Builder theme variant for Popup that surrounds the results list */
    resultsClass: PropTypes.string,
    /** CSS class for the PopUp with results */
    value: PropTypes.string,
    /** Value passed to input field */
    useUserLocation: PropTypes.bool,
    /** If true, and props.near is nullish, attempts to use browser's geolocation api to bias results to those nearby user */
    near: PropTypes.string,
    /** 'lon,lat' format - if present, biases results to those near the given lon,lat */
    source: PropTypes.string,
    /** Which places api to use. Currently only mapbox is supported  */
    apiKey: PropTypes.string,
    /** API key for places API (defaults to value of MAPBOX_APIKEY from env) */
    endpoint: PropTypes.string,
    /** API endpoint (defaults to mapbox v5) */
    disabled: PropTypes.bool,
    /** disables the input field */
    hint: PropTypes.string,
    /** input field hint */
    focus: PropTypes.bool,
    /** input field focus */
    icon: PropTypes.any,
    iconOpts: PropTypes.object,
    imageOpts: PropTypes.object
  };
  static defaultProps = {
    className: null,
    eventData: null,
    inputClass: null,
    inputVariant: null,
    resultsVariant: 'geoCoderResults',
    resultsClass: null,
    onInputChange: () => {},
    onResults: () => {},
    value: '',
    useUserLocation: true,
    near: '',
    source: 'mapbox',
    apiKey: MAPBOX_APIKEY,
    endpoint: MAPBOX_QUERY_ENDPOINT,
    queryEndpoint: MAPBOX_QUERY_ENDPOINT,
    retrieveEndpoint: MAPBOX_RETRIEVE_ENDPOINT,
    disabled: false,
    hint: ''
  };
  constructor(props) {
    super(props);
    const {
      source
    } = this.props;
    this.query = Geocoder.getQueryBySource(source);
    this.retrieve = Geocoder.getRetriverBySource(source);
    this.state = {
      results: {
        suggestions: []
      },
      query: props.value,
      currentLocation: null,
      sessionToken: v4()
    };
  }
  componentDidMount() {
    const {
      useUserLocation
    } = this.props;
    if (useUserLocation) {
      this.getUserLocation();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      useUserLocation,
      value
    } = this.props;
    if (useUserLocation && !prevProps.useUserLocation) {
      this.getUserLocation();
    }
    if (!useUserLocation && prevProps.useUserLocation && this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
    }
    if (value !== prevProps.value) {
      this.setState({
        query: value
      }); // eslint-disable-line
    }
  }
  componentWillUnmount() {
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
    }
  }
  setCurrentLocation = currentLocation => {
    this.setState({
      currentLocation
    });
  };
  getUserLocation = () => {
    if (navigator.geolocation) {
      this.watchId = navigator.geolocation.watchPosition(this.setCurrentLocation);
    }
  };
  getProximity = () => {
    const {
      useUserLocation
    } = this.props;
    const {
      currentLocation
    } = this.state;
    if (useUserLocation && currentLocation) {
      const lat = currentLocation?.coords?.latitude;
      const lon = currentLocation?.coords?.longitude;
      if (lat && lon) {
        return `${lon},${lat}`;
      }
    }
    return '';
  };
  getQueryOpts = () => {
    const {
      near,
      apiKey,
      endpoint,
      queryEndpoint
    } = this.props;
    const {
      sessionToken
    } = this.state;
    const proximity = near || this.getProximity();
    return {
      apiKey,
      endpoint: queryEndpoint || endpoint,
      near: proximity,
      sessionToken
    };
  };
  getRetrieveOpts = () => {
    const {
      apiKey,
      retrieveEndpoint
    } = this.props;
    const {
      sessionToken
    } = this.state;
    return {
      apiKey,
      endpoint: retrieveEndpoint,
      sessionToken
    };
  };
  onSearch = async (evt, {
    setOpened,
    isOpened
  }) => {
    const {
      onInputChange,
      onResults
    } = this.props;
    const {
      value: query
    } = evt;
    onInputChange(evt);
    this.setState({
      query
    });
    if (!query) return;
    const queryOpts = this.getQueryOpts();
    const results = await this.query(query, queryOpts);
    if (results.suggestions.length && !isOpened) {
      setOpened(true);
    }
    onResults(results.suggestions);
    this.setState({
      results
    });
  };

  /**
   * @returns {object} { value : <place information>, data : <eventData supplied as prop> }
   */
  onSelect = async ({
    data
  }) => {
    const {
      onChange,
      eventData
    } = this.props;
    this.setState({
      query: data.location
    });
    const retrieveOpts = this.getRetrieveOpts();
    const item = await this.retrieve(data.mapbox_id, retrieveOpts);
    if (typeof onChange === 'function') {
      onChange({
        value: item,
        data: eventData
      });
    }
  };
  renderGeocoder = ({
    ref,
    setOpened,
    isOpened
  }) => {
    const {
      className,
      inputClass,
      inputVariant,
      disabled,
      hint,
      focus,
      icon,
      iconOpts,
      imageOpts
    } = this.props;
    const {
      query
    } = this.state;
    this.setOpened = setOpened;
    const IClass = icon ? CompositeTextInput : TextInput;
    const input = /*#__PURE__*/React.createElement(IClass, {
      hint: hint,
      focus: focus,
      icon: icon,
      iconOpts: iconOpts,
      imageOpts: imageOpts,
      value: query,
      className: classnames(S.input, inputClass),
      alwaysFireOnReturn: false,
      variant: inputVariant,
      disabled: disabled,
      changeOnReturn: false,
      onChange: q => this.onSearch(q, {
        setOpened,
        isOpened
      })
    });
    return /*#__PURE__*/React.createElement("div", {
      ref: ref,
      className: className
    }, input);
  };
  renderResults = (results = []) => {
    const items = results.suggestions.map(place => /*#__PURE__*/React.createElement(Popup.Item, {
      eventData: place,
      onClick: this.onSelect,
      key: place.id
    }, /*#__PURE__*/React.createElement(Flex, {
      variant: "colS"
    }, /*#__PURE__*/React.createElement(Text, null, place.location), /*#__PURE__*/React.createElement(Text, {
      variant: "caption"
    }, place.place_formatted))));
    if (results.attribution) {
      return items.concat(/*#__PURE__*/React.createElement(Popup.Header, null, /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 10,
          whiteSpace: 'normal',
          width: 'min-content'
        }
      }, results.attribution)));
    }
    return items;
  };
  render() {
    const {
      resultsVariant,
      resultsClass
    } = this.props;
    const {
      results
    } = this.state;
    const items = this.renderResults(results);
    return /*#__PURE__*/React.createElement(Popup, {
      setPopupMinWidth: true,
      setPopupMaxWidth: true,
      className: resultsClass,
      variant: resultsVariant,
      trigger: this.renderGeocoder
    }, items);
  }
}